import type { Account, AccountLedgerEntry, BalanceAudit, Budget, CrDrEntry, MonthlyPeriod, Plan, PlanPayment, Transaction } from '../types';
import { buildBudgetUsage } from './budgets.js';
import { addDays, addMonths, monthKey as monthKeyForDate, normalizeDateISO, todayISO } from './date.js';
import type { ReportSourceData } from './reports';

export type FinancialHealthSeverity = 'critical' | 'warning' | 'info';
export type FinancialHealthStatus = 'Healthy' | 'Review Recommended' | 'Needs Attention' | 'Critical';
export type FinancialHealthDomain = 'transaction' | 'account' | 'transfer' | 'card_payment' | 'plan' | 'crdr' | 'budget' | 'reconciliation' | 'configuration' | 'period';

export type FinancialHealthIssue = {
  id: string;
  severity: FinancialHealthSeverity;
  domain: FinancialHealthDomain;
  code: string;
  title: string;
  description: string;
  count?: number;
  amount?: number;
  entityId?: string;
  entityIds?: string[];
  dateISO?: string;
  monthKey?: string;
  recommendedAction?: string;
  evidence?: Array<Record<string, string | number>>;
};

export type FinancialHealthModel = {
  status: FinancialHealthStatus;
  evaluatedAtISO: string;
  defaultMonthKey: string;
  analysisScope: {
    structuralIntegrity: string;
    operationalWarnings: string;
    duplicateDetection: string;
    reconciliation: string;
  };
  issueCounts: Record<FinancialHealthSeverity, number>;
  issues: FinancialHealthIssue[];
  groups: Array<{ domain: FinancialHealthDomain; title: string; issues: FinancialHealthIssue[] }>;
  summaries: {
    receivable: number;
    payable: number;
    overduePlanAmount: number;
    budgetOverspend: number;
    affectedTransactionAmount: number;
  };
};

const severityRank: Record<FinancialHealthSeverity, number> = { critical: 0, warning: 1, info: 2 };
const domainTitles: Record<FinancialHealthDomain, string> = {
  transaction: 'Transactions',
  account: 'Accounts & Cards',
  transfer: 'Transfers',
  card_payment: 'Credit-card Payments',
  plan: 'Plans',
  crdr: 'CR/DR',
  budget: 'Budgets',
  reconciliation: 'Reconciliation',
  configuration: 'Configuration',
  period: 'Monthly Periods'
};

const sum = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((total, row) => total + Number(pick(row) || 0), 0);
const clean = (value: unknown) => String(value || '').trim();
const normalized = (value: unknown) => clean(value).toLowerCase().replace(/\s+/g, ' ');
const activeTx = (row: Transaction) => row.status !== 'deleted' && row.active !== false;
const activePlan = (plan: Plan) => plan.status === 'active' || plan.status === 'Active';
const activeBudget = (budget: Budget) => budget.status !== 'deleted' && budget.active !== false;
const inMonth = (date: string | undefined, key: string) => monthKeyForDate(normalizeDateISO(date)) === key;

export function overallHealthStatus(issues: Pick<FinancialHealthIssue, 'severity'>[]): FinancialHealthStatus {
  if (issues.some((issue) => issue.severity === 'critical')) return 'Critical';
  if (issues.some((issue) => issue.severity === 'warning')) return 'Needs Attention';
  if (issues.some((issue) => issue.severity === 'info')) return 'Review Recommended';
  return 'Healthy';
}

const issue = (input: FinancialHealthIssue | undefined) => input as FinancialHealthIssue;

function groupLinkedRows(rows: AccountLedgerEntry[], field: 'transferId' | 'cardPaymentId') {
  const groups = new Map<string, AccountLedgerEntry[]>();
  rows.forEach((row) => {
    const key = clean(row[field]);
    if (!key) return;
    groups.set(key, [...(groups.get(key) || []), row]);
  });
  return groups;
}

export function brokenTransferIssues(rows: AccountLedgerEntry[], accountIds = new Set<string>()): FinancialHealthIssue[] {
  return Array.from(groupLinkedRows(rows, 'transferId')).map(([transferId, group]) => {
    const debit = group.filter((row) => row.type === 'transfer_out' && row.direction === 'debit');
    const credit = group.filter((row) => row.type === 'transfer_in' && row.direction === 'credit');
    const valid = group.length === 2
      && debit.length === 1
      && credit.length === 1
      && Number(debit[0].amount || 0) === Number(credit[0].amount || 0)
      && debit[0].relatedAccountId === credit[0].accountId
      && credit[0].relatedAccountId === debit[0].accountId
      && (!accountIds.size || (accountIds.has(debit[0].accountId) && accountIds.has(credit[0].accountId)))
      && normalizeDateISO(debit[0].dateISO || debit[0].date) === normalizeDateISO(credit[0].dateISO || credit[0].date);
    if (valid) return undefined;
    return {
      id: `transfer-broken-${transferId}`,
      severity: 'critical',
      domain: 'transfer',
      code: 'BROKEN_TRANSFER_PAIR',
      title: 'Broken transfer pair',
      description: `${group.length} ledger row(s) found for transfer ${transferId}. Expected one debit and one credit with matching amount and accounts.`,
      count: group.length,
      amount: sum(group, (row) => row.amount) / Math.max(group.length, 1),
      entityId: transferId,
      entityIds: group.map((row) => row.id),
      dateISO: group[0] ? normalizeDateISO(group[0].dateISO || group[0].date) : undefined,
      recommendedAction: 'Review Transfer',
      evidence: group.map((row) => ({ id: row.id, type: row.type, direction: row.direction, amount: row.amount, accountId: row.accountId, relatedAccountId: row.relatedAccountId || '' }))
    } satisfies FinancialHealthIssue;
  }).filter(Boolean) as FinancialHealthIssue[];
}

export function brokenCardPaymentIssues(rows: AccountLedgerEntry[], accounts: Account[] = []): FinancialHealthIssue[] {
  const accountMap = new Map(accounts.map((account) => [account.id, account]));
  return Array.from(groupLinkedRows(rows, 'cardPaymentId')).map(([cardPaymentId, group]) => {
    const debit = group.filter((row) => row.type === 'credit_card_payment' && row.direction === 'debit');
    const credit = group.filter((row) => row.type === 'credit_card_payment' && row.direction === 'credit');
    const source = debit[0] ? accountMap.get(debit[0].accountId) : undefined;
    const card = credit[0] ? accountMap.get(credit[0].accountId) : undefined;
    const valid = group.length === 2
      && debit.length === 1
      && credit.length === 1
      && Number(debit[0].amount || 0) === Number(credit[0].amount || 0)
      && debit[0].relatedAccountId === credit[0].accountId
      && credit[0].relatedAccountId === debit[0].accountId
      && (!accounts.length || (source && source.type !== 'credit_card' && card && card.type === 'credit_card'))
      && normalizeDateISO(debit[0].dateISO || debit[0].date) === normalizeDateISO(credit[0].dateISO || credit[0].date);
    if (valid) return undefined;
    return {
      id: `card-payment-broken-${cardPaymentId}`,
      severity: 'critical',
      domain: 'card_payment',
      code: 'BROKEN_CARD_PAYMENT_PAIR',
      title: 'Broken credit-card payment pair',
      description: `${group.length} ledger row(s) found for card payment ${cardPaymentId}. Expected paying-account debit and card credit rows.`,
      count: group.length,
      amount: sum(group, (row) => row.amount) / Math.max(group.length, 1),
      entityId: cardPaymentId,
      entityIds: group.map((row) => row.id),
      dateISO: group[0] ? normalizeDateISO(group[0].dateISO || group[0].date) : undefined,
      recommendedAction: 'Review Card Payment',
      evidence: group.map((row) => ({ id: row.id, type: row.type, direction: row.direction, amount: row.amount, accountId: row.accountId, relatedAccountId: row.relatedAccountId || '' }))
    } satisfies FinancialHealthIssue;
  }).filter(Boolean) as FinancialHealthIssue[];
}

export function possibleDuplicateGroups(transactions: Transaction[], scopeMonthKey: string) {
  const groups = new Map<string, Transaction[]>();
  transactions
    .filter(activeTx)
    .filter((row) => inMonth(row.dateISO || row.date, scopeMonthKey))
    .filter((row) => row.source !== 'PlanPayment' && !row.refId)
    .filter((row) => !row.note || !normalized(row.note).startsWith('paid:'))
    .forEach((row) => {
      const key = [normalizeDateISO(row.dateISO || row.date), row.type, Number(row.amount || 0).toFixed(2), row.accountId || '', normalized(row.category), normalized(row.note)].join('|');
      groups.set(key, [...(groups.get(key) || []), row]);
    });
  return Array.from(groups.values())
    .filter((rows) => rows.length > 1)
    .sort((a, b) => b.length - a.length || Number(b[0].amount || 0) - Number(a[0].amount || 0));
}

const latestAuditMap = (audits: BalanceAudit[]) => {
  const map = new Map<string, BalanceAudit>();
  audits.forEach((audit) => {
    const existing = map.get(audit.accountId);
    if (!existing || normalizeDateISO(audit.dateISO || audit.auditDate) > normalizeDateISO(existing.dateISO || existing.auditDate)) map.set(audit.accountId, audit);
  });
  return map;
};

export function buildFinancialHealthModel(source: ReportSourceData, now = todayISO()): FinancialHealthModel {
  const today = normalizeDateISO(now);
  const currentMonth = monthKeyForDate(today);
  const previousMonth = addMonths(`${currentMonth}-01`, -1).slice(0, 7);
  const accounts = source.accounts.filter((account) => account.status !== 'deleted');
  const activeAccounts = accounts.filter((account) => account.status === 'active' && account.active !== false);
  const accountMap = new Map(accounts.map((account) => [account.id, account]));
  const activeAccountIds = new Set(activeAccounts.map((account) => account.id));
  const transactions = source.transactions.filter(activeTx);
  const currentTransactions = transactions.filter((row) => inMonth(row.dateISO || row.date, currentMonth));
  const ledger = source.accountLedger.filter((row) => Number(row.amount || 0) !== 0);
  const audits = source.balanceAudits || [];
  const monthlyPeriods = source.monthlyPeriods || [];
  const periodsByKey = new Map(monthlyPeriods.map((period) => [period.monthKey, period]));
  const issues: FinancialHealthIssue[] = [];

  const missingAccountTx = transactions.filter((row) => row.accountId && !accountMap.has(row.accountId));
  issues.push(issue(missingAccountTx.length ? {
    id: 'transaction-missing-account-reference',
    severity: 'critical',
    domain: 'transaction',
    code: 'MISSING_ACCOUNT_REFERENCE',
    title: 'Transactions reference missing accounts',
    description: `${missingAccountTx.length} transaction(s) point to an account document that no longer exists.`,
    count: missingAccountTx.length,
    amount: sum(missingAccountTx, (row) => row.amount),
    entityIds: missingAccountTx.map((row) => row.id),
    dateISO: missingAccountTx.sort((a, b) => normalizeDateISO(a.dateISO || a.date).localeCompare(normalizeDateISO(b.dateISO || b.date)))[0]?.dateISO,
    recommendedAction: 'Open Transactions',
    evidence: missingAccountTx.slice(0, 10).map((row) => ({ id: row.id, date: normalizeDateISO(row.dateISO || row.date), amount: row.amount, missingAccountId: row.accountId || '' }))
  } : undefined));

  const ledgerMissingAccount = ledger.filter((row) => row.accountId && !accountMap.has(row.accountId));
  issues.push(issue(ledgerMissingAccount.length ? {
    id: 'ledger-missing-account-reference',
    severity: 'critical',
    domain: 'account',
    code: 'LEDGER_MISSING_ACCOUNT_REFERENCE',
    title: 'Ledger rows reference missing accounts',
    description: `${ledgerMissingAccount.length} account-ledger row(s) reference missing account documents.`,
    count: ledgerMissingAccount.length,
    amount: sum(ledgerMissingAccount, (row) => row.amount),
    entityIds: ledgerMissingAccount.map((row) => row.id),
    recommendedAction: 'Review Account Statement',
    evidence: ledgerMissingAccount.slice(0, 10).map((row) => ({ id: row.id, type: row.type, accountId: row.accountId, amount: row.amount }))
  } : undefined));

  issues.push(...brokenTransferIssues(ledger, new Set(accounts.map((account) => account.id))));
  issues.push(...brokenCardPaymentIssues(ledger, accounts));

  const unassigned = currentTransactions.filter((row) => !row.accountId);
  issues.push(issue(unassigned.length ? {
    id: 'transaction-unassigned-account',
    severity: 'warning',
    domain: 'transaction',
    code: 'UNASSIGNED_TRANSACTION_ACCOUNT',
    title: 'Transactions are not assigned to an account',
    description: `${unassigned.length} current-month transaction(s) do not have a linked account, so statements may be incomplete.`,
    count: unassigned.length,
    amount: sum(unassigned, (row) => row.amount),
    entityIds: unassigned.map((row) => row.id),
    dateISO: unassigned.sort((a, b) => normalizeDateISO(a.dateISO || a.date).localeCompare(normalizeDateISO(b.dateISO || b.date)))[0]?.dateISO,
    monthKey: currentMonth,
    recommendedAction: 'Review Transactions',
    evidence: unassigned.slice(0, 10).map((row) => ({ id: row.id, date: normalizeDateISO(row.dateISO || row.date), type: row.type, category: row.category || '', amount: row.amount }))
  } : undefined));

  const missingCategory = currentTransactions.filter((row) => !clean(row.category));
  issues.push(issue(missingCategory.length ? {
    id: 'transaction-missing-category',
    severity: 'info',
    domain: 'transaction',
    code: 'MISSING_CATEGORY',
    title: 'Transactions missing category',
    description: `${missingCategory.length} current-month transaction(s) have no category.`,
    count: missingCategory.length,
    amount: sum(missingCategory, (row) => row.amount),
    entityIds: missingCategory.map((row) => row.id),
    monthKey: currentMonth,
    recommendedAction: 'Review Transactions'
  } : undefined));

  const missingPaymentMode = currentTransactions.filter((row) => !clean(row.paymentMode));
  issues.push(issue(missingPaymentMode.length ? {
    id: 'transaction-missing-payment-mode',
    severity: 'info',
    domain: 'transaction',
    code: 'MISSING_PAYMENT_MODE',
    title: 'Transactions missing payment mode',
    description: `${missingPaymentMode.length} current-month transaction(s) have no payment mode.`,
    count: missingPaymentMode.length,
    amount: sum(missingPaymentMode, (row) => row.amount),
    entityIds: missingPaymentMode.map((row) => row.id),
    monthKey: currentMonth,
    recommendedAction: 'Review Transactions'
  } : undefined));

  const invalidAmount = transactions.filter((row) => Number(row.amount || 0) <= 0);
  issues.push(issue(invalidAmount.length ? {
    id: 'transaction-invalid-amount',
    severity: 'critical',
    domain: 'transaction',
    code: 'INVALID_TRANSACTION_AMOUNT',
    title: 'Transactions have invalid amounts',
    description: `${invalidAmount.length} active transaction(s) have amount less than or equal to zero.`,
    count: invalidAmount.length,
    entityIds: invalidAmount.map((row) => row.id),
    recommendedAction: 'Review Transactions'
  } : undefined));

  const duplicates = possibleDuplicateGroups(transactions, currentMonth);
  issues.push(issue(duplicates.length ? {
    id: 'transaction-possible-duplicates',
    severity: 'info',
    domain: 'transaction',
    code: 'POSSIBLE_DUPLICATE',
    title: 'Possible duplicate transactions',
    description: `${duplicates.length} high-confidence duplicate-looking group(s) found in ${currentMonth}. Plan payments and linked movements are excluded.`,
    count: duplicates.reduce((total, group) => total + group.length, 0),
    amount: sum(duplicates.flat(), (row) => row.amount),
    monthKey: currentMonth,
    recommendedAction: 'Review Transactions',
    evidence: duplicates.slice(0, 8).flatMap((group, index) => group.map((row) => ({ group: index + 1, id: row.id, date: normalizeDateISO(row.dateISO || row.date), amount: row.amount, category: row.category || '', note: row.note || '' })))
  } : undefined));

  const negativeLiquid = activeAccounts.filter((account) => account.type !== 'credit_card' && Number(account.currentBalance || 0) < 0);
  issues.push(...negativeLiquid.map((account) => ({
    id: `account-negative-${account.id}`,
    severity: 'warning' as const,
    domain: 'account' as const,
    code: 'NEGATIVE_LIQUID_BALANCE',
    title: `${account.name} has negative balance`,
    description: `Liquid account balance is ${Number(account.currentBalance || 0)}. Credit-card outstanding is handled separately.`,
    amount: Math.abs(Number(account.currentBalance || 0)),
    entityId: account.id,
    recommendedAction: 'Open Account Statement'
  })));

  const cards = activeAccounts.filter((account) => account.type === 'credit_card');
  issues.push(...cards.flatMap((card) => {
    const creditLimit = Number(card.creditLimit || 0);
    const outstanding = Math.max(Number(card.currentBalance || 0), 0);
    const utilizationPct = creditLimit > 0 ? Math.round((outstanding / creditLimit) * 100) : 0;
    return [
      issue(creditLimit <= 0 ? {
        id: `card-missing-limit-${card.id}`,
        severity: 'info',
        domain: 'configuration',
        code: 'CARD_MISSING_LIMIT',
        title: `${card.name} is missing credit limit`,
        description: 'Credit utilization and available-limit checks need a credit limit.',
        entityId: card.id,
        recommendedAction: 'Open Settings'
      } : undefined),
      issue(creditLimit > 0 && utilizationPct >= 100 ? {
        id: `card-over-limit-${card.id}`,
        severity: 'warning',
        domain: 'account',
        code: 'CARD_OVER_LIMIT',
        title: `${card.name} is at or above credit limit`,
        description: `Utilization is ${utilizationPct}%.`,
        amount: outstanding,
        entityId: card.id,
        recommendedAction: 'Review Card Statement'
      } : creditLimit > 0 && utilizationPct >= 80 ? {
        id: `card-high-utilization-${card.id}`,
        severity: 'warning',
        domain: 'account',
        code: 'CARD_HIGH_UTILIZATION',
        title: `${card.name} utilization is above 80%`,
        description: `Utilization is ${utilizationPct}%.`,
        amount: outstanding,
        entityId: card.id,
        recommendedAction: 'Review Card Statement'
      } : undefined)
    ].filter(Boolean) as FinancialHealthIssue[];
  }));

  const latestAudit = latestAuditMap(audits);
  const currentOrPreviousAudits = audits.filter((audit) => audit.monthKey === currentMonth || audit.monthKey === previousMonth);
  const auditMismatches = currentOrPreviousAudits.filter((audit) => Math.abs(Number(audit.difference || 0)) > 0 || Math.abs(Number(audit.availableLimitDifference || 0)) > 0);
  const closedOrReviewed = new Set(monthlyPeriods.filter((period) => period.status === 'CLOSED' || period.status === 'REVIEWED').map((period) => period.monthKey));
  issues.push(issue(auditMismatches.length ? {
    id: 'reconciliation-mismatch',
    severity: auditMismatches.some((audit) => closedOrReviewed.has(audit.monthKey)) ? 'critical' : 'warning',
    domain: 'reconciliation',
    code: 'RECONCILIATION_MISMATCH',
    title: 'Balance audit mismatch',
    description: `${auditMismatches.length} current/previous month audit snapshot(s) do not match system balance.`,
    count: auditMismatches.length,
    amount: sum(auditMismatches, (audit) => Math.abs(Number(audit.difference || 0)) + Math.abs(Number(audit.availableLimitDifference || 0))),
    recommendedAction: 'Open Monthly Close',
    evidence: auditMismatches.slice(0, 10).map((audit) => ({ id: audit.id, account: audit.accountName, monthKey: audit.monthKey, difference: audit.difference || 0, availableDifference: audit.availableLimitDifference || 0 }))
  } : undefined));

  const neverReconciled = activeAccounts.filter((account) => !latestAudit.has(account.id));
  issues.push(issue(neverReconciled.length ? {
    id: 'reconciliation-never-reconciled',
    severity: 'warning',
    domain: 'reconciliation',
    code: 'NEVER_RECONCILED',
    title: 'Accounts not reconciled yet',
    description: `${neverReconciled.length} active account(s) have no balance audit snapshot.`,
    count: neverReconciled.length,
    entityIds: neverReconciled.map((account) => account.id),
    recommendedAction: 'Open Monthly Close'
  } : undefined));

  const activePlans = source.plans.filter(activePlan);
  const overduePlans = activePlans.filter((plan) => normalizeDateISO(plan.nextDueDate || plan.dueDate) < today);
  issues.push(issue(overduePlans.length ? {
    id: 'plan-overdue',
    severity: 'warning',
    domain: 'plan',
    code: 'OVERDUE_PLAN',
    title: 'Plans are overdue',
    description: `${overduePlans.length} active plan(s) are past due. Paused plans are ignored.`,
    count: overduePlans.length,
    amount: sum(overduePlans, (plan) => plan.amount),
    dateISO: overduePlans.sort((a, b) => normalizeDateISO(a.nextDueDate || a.dueDate).localeCompare(normalizeDateISO(b.nextDueDate || b.dueDate)))[0]?.nextDueDate,
    recommendedAction: 'Open Plans',
    evidence: overduePlans.slice(0, 10).map((plan) => ({ id: plan.id, title: plan.title, dueDate: normalizeDateISO(plan.nextDueDate || plan.dueDate), amount: plan.amount }))
  } : undefined));

  const plansMissingAccount = activePlans.filter((plan) => !plan.paidFromAccountId);
  issues.push(issue(plansMissingAccount.length ? {
    id: 'plan-missing-paid-from-account',
    severity: 'info',
    domain: 'plan',
    code: 'PLAN_MISSING_ACCOUNT',
    title: 'Plans missing paid-from account',
    description: `${plansMissingAccount.length} active plan(s) will not deduct an account automatically when marked paid.`,
    count: plansMissingAccount.length,
    amount: sum(plansMissingAccount, (plan) => plan.amount),
    entityIds: plansMissingAccount.map((plan) => plan.id),
    recommendedAction: 'Open Plans'
  } : undefined));

  const plansMissingPaymentMode = activePlans.filter((plan) => !clean(plan.paymentMode));
  issues.push(issue(plansMissingPaymentMode.length ? {
    id: 'plan-missing-payment-mode',
    severity: 'info',
    domain: 'plan',
    code: 'PLAN_MISSING_PAYMENT_MODE',
    title: 'Plans missing payment mode',
    description: `${plansMissingPaymentMode.length} active plan(s) have no default payment mode.`,
    count: plansMissingPaymentMode.length,
    entityIds: plansMissingPaymentMode.map((plan) => plan.id),
    recommendedAction: 'Open Plans'
  } : undefined));

  const inactivePlanAccounts = activePlans.filter((plan) => plan.paidFromAccountId && !activeAccountIds.has(plan.paidFromAccountId));
  issues.push(issue(inactivePlanAccounts.length ? {
    id: 'plan-inactive-paid-from-account',
    severity: 'critical',
    domain: 'configuration',
    code: 'PLAN_INACTIVE_ACCOUNT',
    title: 'Plans reference inactive or missing accounts',
    description: `${inactivePlanAccounts.length} active plan(s) reference an account that is not active.`,
    count: inactivePlanAccounts.length,
    entityIds: inactivePlanAccounts.map((plan) => plan.id),
    recommendedAction: 'Open Plans'
  } : undefined));

  const openCrdr = source.crdr.filter((row) => row.status !== 'Settled');
  const oldOpenCrdr = openCrdr.filter((row) => normalizeDateISO(row.date) < addDays(today, -60));
  issues.push(issue(oldOpenCrdr.length ? {
    id: 'crdr-old-open',
    severity: 'info',
    domain: 'crdr',
    code: 'OLD_OPEN_CRDR',
    title: 'Old open CR/DR items',
    description: `${oldOpenCrdr.length} open CR/DR item(s) are older than 60 days.`,
    count: oldOpenCrdr.length,
    amount: sum(oldOpenCrdr, (row) => row.amount),
    recommendedAction: 'Open CR/DR',
    evidence: oldOpenCrdr.slice(0, 10).map((row) => ({ id: row.id, name: row.contactName || row.name, type: row.type, date: row.date, amount: row.amount }))
  } : undefined));

  const currentBudgets = source.budgets.filter(activeBudget).filter((budget) => budget.monthKey === currentMonth);
  const budgetUsage = buildBudgetUsage(currentBudgets, currentTransactions);
  const exceededBudgets = budgetUsage.filter((budget) => budget.state === 'crossed');
  const warningBudgets = budgetUsage.filter((budget) => budget.state === 'warning');
  issues.push(issue(exceededBudgets.length ? {
    id: 'budget-exceeded',
    severity: 'warning',
    domain: 'budget',
    code: 'BUDGET_EXCEEDED',
    title: 'Budget categories crossed 100%',
    description: `${exceededBudgets.length} budget categor(y/ies) exceeded the monthly limit.`,
    count: exceededBudgets.length,
    amount: sum(exceededBudgets, (budget) => Math.abs(Math.min(budget.remaining, 0))),
    monthKey: currentMonth,
    recommendedAction: 'Open Budgets'
  } : undefined));
  issues.push(issue(warningBudgets.length ? {
    id: 'budget-warning',
    severity: 'info',
    domain: 'budget',
    code: 'BUDGET_ABOVE_80',
    title: 'Budget categories above 80%',
    description: `${warningBudgets.length} budget categor(y/ies) are above the warning threshold.`,
    count: warningBudgets.length,
    monthKey: currentMonth,
    recommendedAction: 'Open Budgets'
  } : undefined));

  const hasAnyFinanceSetup = source.accounts.length > 0 || source.transactions.length > 0 || source.plans.length > 0 || source.accountLedger.length > 0;
  const hasActiveAccount = activeAccounts.length > 0;
  issues.push(issue(hasAnyFinanceSetup && !hasActiveAccount ? {
    id: 'configuration-no-active-account',
    severity: 'warning',
    domain: 'configuration',
    code: 'NO_ACTIVE_ACCOUNT',
    title: 'No active financial account',
    description: 'Account-linked tracking needs at least one active bank, cash, wallet, or credit-card account.',
    recommendedAction: 'Open Settings'
  } : undefined));

  const monthKeysWithData = new Set(transactions.map((row) => row.monthKey).filter(Boolean));
  monthKeysWithData.forEach((key) => {
    if (key >= currentMonth) return;
    const period = periodsByKey.get(key);
    if (!period) {
      issues.push({
        id: `period-open-${key}`,
        severity: 'info',
        domain: 'period',
        code: 'PAST_MONTH_NOT_REVIEWED',
        title: `${key} has not been reviewed`,
        description: 'Past month has transaction data but no monthly period review record.',
        monthKey: key,
        recommendedAction: 'Open Monthly Close'
      });
    }
  });
  monthlyPeriods.forEach((period) => {
    if (period.status === 'REOPENED') {
      issues.push({
        id: `period-reopened-${period.monthKey}`,
        severity: 'info',
        domain: 'period',
        code: 'PERIOD_REOPENED',
        title: `${period.monthKey} is reopened`,
        description: period.reopenReason || 'This period is open for corrections and has not been reclosed.',
        monthKey: period.monthKey,
        recommendedAction: 'Open Monthly Close'
      });
    }
    if (period.status === 'REVIEWED' && period.monthKey < currentMonth) {
      issues.push({
        id: `period-reviewed-not-closed-${period.monthKey}`,
        severity: 'info',
        domain: 'period',
        code: 'REVIEWED_NOT_CLOSED',
        title: `${period.monthKey} is reviewed but not closed`,
        description: 'Reviewed period remains editable until closed.',
        monthKey: period.monthKey,
        recommendedAction: 'Open Monthly Close'
      });
    }
  });

  const sorted = issues
    .filter(Boolean)
    .sort((a, b) => severityRank[a.severity] - severityRank[b.severity] || Number(b.amount || 0) - Number(a.amount || 0) || a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
  const issueCounts = {
    critical: sorted.filter((row) => row.severity === 'critical').length,
    warning: sorted.filter((row) => row.severity === 'warning').length,
    info: sorted.filter((row) => row.severity === 'info').length
  };
  const groups = (Object.keys(domainTitles) as FinancialHealthDomain[])
    .map((domain) => ({ domain, title: domainTitles[domain], issues: sorted.filter((row) => row.domain === domain) }))
    .filter((group) => group.issues.length);

  return {
    status: overallHealthStatus(sorted),
    evaluatedAtISO: new Date(`${today}T00:00:00`).toISOString(),
    defaultMonthKey: currentMonth,
    analysisScope: {
      structuralIntegrity: 'All loaded user-scoped linked data',
      operationalWarnings: `Current month ${currentMonth} plus active overdue plans`,
      duplicateDetection: `Current month ${currentMonth}, high-confidence exact groups only`,
      reconciliation: `Current and previous month audits: ${currentMonth}, ${previousMonth}`
    },
    issueCounts,
    issues: sorted,
    groups,
    summaries: {
      receivable: sum(openCrdr.filter((row) => row.type === 'CR'), (row) => row.amount),
      payable: sum(openCrdr.filter((row) => row.type === 'DR'), (row) => row.amount),
      overduePlanAmount: sum(overduePlans, (plan) => plan.amount),
      budgetOverspend: sum(exceededBudgets, (budget) => Math.abs(Math.min(budget.remaining, 0))),
      affectedTransactionAmount: sum(missingAccountTx.concat(unassigned), (row) => row.amount)
    }
  };
}
